import { useEffect, useRef, useState } from "react";
import { recordExport } from "../digitizingServiceStorage";
import { usePostHog } from "posthog-js/react";
import { mediaUrl } from "../mediaUrl";
import { usePageMeta } from "../usePageMeta";
import { FormatDownloadButtons } from "../components/FormatDownloadButtons";
import { DownsampleNotice } from "../components/DownsampleNotice";
import { ShareButton } from "../components/ShareButton";
import { StudioLink } from "../components/StudioLink";
import { EmailDesignModal } from "../components/EmailDesignModal";
import { ProUpsellCard } from "../components/ProUpsellCard";
import { useDownloadPrompts } from "../useDownloadPrompts";
import { CONVERT_PAIRS, type ConvertPair, convertPairPath, convertPairStructuredData } from "../convertPairs";

const PAGE_TITLE =
  "Free Embroidery File Converter — PES, DST, JEF, EXP, VP3 | Embroidery Online";
// Kept under ~160 characters -- see the matching note on HomePage.tsx.
const PAGE_DESCRIPTION =
  "Convert PES, DST, JEF, EXP, and VP3 embroidery files for free -- including PES to DST. Upload, preview, resize, and download the format your machine needs.";

interface FAQItem {
  question: string;
  answer: string;
}

// Deliberately distinct wording from the "Supported conversions" prose above
// -- see the matching note on CreatePage.tsx's FAQ_DATA for why a FAQ answer
// and a body paragraph covering the same ground must not be the same
// paragraph repeated.
const FAQ_DATA: FAQItem[] = [
  {
    question: "Can I convert a PES file to DST?",
    answer: "Yes -- upload the PES file above and download it as DST, along with EXP and JEF.",
  },
  {
    question: "How do I convert DST to PES?",
    answer: "Upload the DST file and download the PES. DST doesn't store thread colors, so the PES gets gray placeholders to change on your machine.",
  },
  {
    question: "Can I convert a JEF file to PES?",
    answer: "Yes. A Janome JEF file converts to PES for Brother machines the same way -- upload it and download the PES.",
  },
  {
    question: "Is this embroidery file converter free?",
    answer: "Yes. Convert as many files as you like, free, with no account required.",
  },
  {
    question: "What embroidery file formats are supported?",
    answer: "PES, DST, JEF, and EXP can be uploaded and converted to any of the others. VP3 can be uploaded and converted from, but not created, since Husqvarna Viking and Pfaff use a closed format.",
  },
  {
    question: "Can I resize a file while converting it?",
    answer: "Yes. Enter a width or height before converting, or leave both blank to keep the original size.",
  },
];

// Module-level, not inline in JSX -- see the matching note on HomePage.tsx.
// UtilityApplication rather than DesignApplication: this page converts
// existing files, it doesn't design anything. FAQPage matches FAQ_DATA
// above rather than being hand-duplicated, same reasoning as
// CreatePage.tsx's STRUCTURED_DATA.
const STRUCTURED_DATA: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Embroidery File Converter",
    url: "https://embroiderytext.com/convert",
    description: PAGE_DESCRIPTION,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any (runs in a web browser)",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_DATA.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  },
];

interface ConvertPageProps {
  apiUrl: string;
  // Minted lazily by App.tsx's ensureToken, not from a bare page load the
  // way /create's is -- see its own comment. Empty until that resolves; the
  // email/Pro plumbing below is simply not eligible until then.
  token: string;
  stitchReplayerEnabled: boolean;
  shareButtonsEnabled: boolean;
  emailCaptureEnabled: boolean;
  // The embroidery_viewer flag: the "view it online" link to /preview.
  embroideryViewerEnabled: boolean;
  proUpsellVariant: string;
  // Set on /convert/{slug} -- one of the format-pair landing pages in
  // convertPairs.ts. Undefined on the generic /convert.
  pair?: ConvertPair;
}

interface ConvertResult {
  source_format: string;
  source_filename: string;
  width_inches: number;
  height_inches: number;
  size_warning: boolean;
  // How many stitches the backend's downsampling pass took out of the
  // files (app/services/downsample.py); 0 when it didn't run.
  stitches_removed?: number;
  files: Record<string, string>;
  content_key: string;
  // Set only when this conversion was made with a token that resolved to a
  // real link -- see /api/convert. Lets this page email/Pro-upsell the
  // conversion the same way CreatePage does with a design's asset id.
  asset_id: string | null;
}

// Kept in sync with app/utils/embroidery.py::READ_FORMATS -- the backend is
// the real gate, this is only so the file picker and an obviously wrong file
// fail fast instead of round-tripping to the server first.
const ACCEPTED_EXTENSIONS = [".pes", ".dst", ".exp", ".jef", ".vp3"];

// Mirrors app/utils/embroidery.py::WRITE_FORMATS and the path shape built by
// app/services/media.py's storage_key/download_url -- documented there as
// stable and permanent (a URL handed to someone must not change), which is
// what makes it safe to reconstruct here instead of asking the backend.
const CONVERTED_FORMATS = ["pes", "dst", "exp", "jef", "png"];

function filesForContentKey(contentKey: string): Record<string, string> {
  const prefix = contentKey.slice(0, 2);
  const files: Record<string, string> = {};
  for (const format of CONVERTED_FORMATS) {
    files[format] = `/assets/media/${prefix}/${contentKey}.${format}`;
  }
  return files;
}

export function ConvertPage({
  apiUrl,
  token,
  stitchReplayerEnabled,
  shareButtonsEnabled,
  emailCaptureEnabled,
  embroideryViewerEnabled,
  proUpsellVariant: proUpsellFlag,
  pair,
}: ConvertPageProps) {
  const pagePath = pair ? convertPairPath(pair) : "/convert";
  const faqData = pair ? pair.faq : FAQ_DATA;
  // The other pair pages, for the "Other conversions" links at the bottom.
  const otherPairs = CONVERT_PAIRS.filter((p) => p !== pair);

  const [file, setFile] = useState<File | null>(null);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [loading, setLoading] = useState(false);
  // The DownsampleNotice checkbox: unticked, requests send ?downsample=false.
  const [downsample, setDownsample] = useState(true);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const posthog = usePostHog();

  const {
    emailPrompt,
    emailPromptSettled,
    proUpsellVariant,
    notifyDownload,
    showEmailDesignButton,
    openEmailPrompt,
    closeEmailPrompt,
    closeProUpsell,
    markProUpsellInterested,
  } = useDownloadPrompts({
    apiUrl,
    token,
    assetId: result?.asset_id,
    emailCaptureEnabled,
    proUpsellFlag,
  });

  // Arriving from /preview's "Convert to another format" link: that page
  // already ran this exact file through /api/convert to get its own
  // preview, so the converted files are already sitting in storage under
  // this content_key -- conversion is content-addressed, so re-uploading
  // would just hash right back to the same key. Reconstructing the result
  // from the URL instead skips that redundant round trip entirely.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contentKey = params.get("content_key");
    if (!contentKey) return;

    const widthInches = parseFloat(params.get("width_inches") || "");
    const heightInches = parseFloat(params.get("height_inches") || "");
    if (!Number.isFinite(widthInches) || !Number.isFinite(heightInches)) return;

    const sourceFormat = params.get("source_format") || "";
    setResult({
      source_format: sourceFormat,
      source_filename: params.get("source_filename") || "",
      width_inches: widthInches,
      height_inches: heightInches,
      size_warning: params.get("size_warning") === "1",
      files: filesForContentKey(contentKey),
      content_key: contentKey,
      // /preview's own conversion already ran without whatever token this
      // page load has -- reusing its content-addressed files carries no
      // asset id, so this page load's email/Pro prompts stay unavailable
      // for it. A fresh conversion (handleSubmit below) mints its own.
      asset_id: null,
    });
    posthog?.capture("embroidery_conversion_preloaded", { source_format: sourceFormat });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePageMeta(
    pair
      ? { title: pair.title, description: pair.description, path: pagePath, structuredData: convertPairStructuredData(pair) }
      : { title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: pagePath, structuredData: STRUCTURED_DATA }
  );

  const extensionOf = (name: string) => {
    const dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot).toLowerCase();
  };

  const handleFileChange = (selected: File | null) => {
    setError("");
    setResult(null);
    if (selected && !ACCEPTED_EXTENSIONS.includes(extensionOf(selected.name))) {
      setFile(null);
      setError(
        `Unsupported file type. Supported formats: ${ACCEPTED_EXTENSIONS.map((e) => e.slice(1)).join(", ").toUpperCase()}`
      );
      return;
    }
    setFile(selected);
  };

  // withDownsample is passed by the notice's checkbox, whose new value isn't
  // in state yet when it re-runs the request.
  const handleSubmit = async (withDownsample: boolean = downsample) => {
    if (!file) {
      setError("Choose a file to convert");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (width) formData.append("width", width);
      if (height) formData.append("height", height);
      if (token) formData.append("token", token);

      const response = await fetch(`${apiUrl}/api/convert${withDownsample ? "" : "?downsample=false"}`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to convert file");
      }

      const data: ConvertResult = await response.json();
      setResult(data);
      posthog?.capture("embroidery_file_converted", {
        source_format: data.source_format,
        target_formats: Object.keys(data.files),
        width_inches: data.width_inches,
        height_inches: data.height_inches,
        stitches_removed: data.stitches_removed,
      });
    } catch (err: any) {
      const message = err.message || "An error occurred";
      setError(message);
      // Its own event rather than folded into a generic error stream: a
      // spike here means the format guard, a corrupt-file case, or the
      // endpoint itself is broken, which "embroidery_file_converted" alone
      // has no way to distinguish from people simply not finishing the flow.
      posthog?.capture("embroidery_conversion_failed", {
        source_extension: extensionOf(file.name),
        error: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (format: string, path: string) => {
    posthog?.capture("embroidery_file_converted_downloaded", {
      format,
      stitches_removed: result?.stitches_removed,
    });

    const downloadUrl = mediaUrl(apiUrl, path);
    const a = document.createElement("a");
    a.href = downloadUrl;
    // file is null for a preloaded (content_key-from-URL) result -- nothing
    // was uploaded on this page load, so fall back to the name it carried
    // over from /preview.
    const baseName = file?.name || result?.source_filename || "embroidery";
    a.download = `${baseName.replace(/\.[^.]+$/, "")}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    notifyDownload(format);
    recordExport("convert");
  };

  const resetFile = () => {
    setFile(null);
    setResult(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Otherwise a stale content_key from a preloaded result stays in the
    // URL after Clear, so reloading (or sharing the link) would silently
    // bring back the old file's conversion instead of the fresh, empty form.
    if (window.location.search) {
      window.history.replaceState({}, "", pagePath);
    }
  };

  return (
    <>
      {pair ? (
        <section className="tool-intro">
          <h1>{pair.heading}</h1>
          <p>{pair.intro}</p>
          <p>
            <small>
              Converting a different format? <a href="/convert">Use the full embroidery file converter →</a>
            </small>
          </p>
          {embroideryViewerEnabled && (
            <p>
              <small>
                Want to preview your design first? <a href="/preview">View it online →</a>
              </small>
            </p>
          )}
        </section>
      ) : (
        <section className="tool-intro">
          <h1>Convert Embroidery Files Online</h1>
          <p>Free PES to DST converter online -- plus DST to PES, JEF to PES, and every other pair between PES, DST, JEF, EXP, and VP3, right in your browser.</p>
          {embroideryViewerEnabled && (
            <p>
              <small>
                Want to preview your design first? <a href="/preview">View it online →</a>
              </small>
            </p>
          )}
        </section>
      )}

      <div className="two-column-layout">
        <div className="column-left">
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <label>
              {pair ? `${pair.from.toUpperCase()} file` : "Embroidery file"}
              <input
                ref={fileInputRef}
                type="file"
                // Only the pair's source format, so the picker opens on the
                // files this page is for. Any other supported format picked
                // anyway (via "All files") still converts -- handleFileChange
                // checks ACCEPTED_EXTENSIONS, not this.
                accept={pair ? `.${pair.from}` : ACCEPTED_EXTENSIONS.join(",")}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                required
              />
            </label>
            <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
              {pair
                ? `Upload a ${pair.from.toUpperCase()} file to get a ${pair.to.toUpperCase()}.`
                : `Supported formats: ${ACCEPTED_EXTENSIONS.map((e) => e.slice(1)).join(", ").toUpperCase()}`}
            </small>

            <div className="form-row">
              <label>
                Width (inches) <small className="label-hint">- optional</small>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="12"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="Original"
                />
              </label>

              <label>
                Height (inches) <small className="label-hint">- optional</small>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="12"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="Original"
                />
              </label>
            </div>

            {width && height && (
              <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-color-amber-500)" }}>
                ⚠️ Warning: Specifying both width and height may stretch or distort the design.
              </small>
            )}

            <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
              Leave both empty to keep the original size.
            </small>

            <button
              type="submit"
              aria-busy={loading}
              disabled={loading || !file}
              style={{ width: "100%", marginTop: "1.25rem" }}
            >
              {loading ? "Converting…" : pair ? `Convert to ${pair.to.toUpperCase()}` : "Preview & Convert"}
            </button>

            {(file || result) && (
              <button
                type="button"
                // Outlined, not filled: this sits directly under the button
                // people actually came to press, and two solid bars of equal
                // weight made "Clear" look like half the point of the page.
                className="secondary outline"
                onClick={resetFile}
                disabled={loading}
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        <div className="column-right">
          <h2 className="column-heading">Preview & Export</h2>

          {error && (
            <article className="error-message">
              <p><strong>Error:</strong> {error}</p>
            </article>
          )}

          {loading && (
            <div className="empty-preview">
              <p aria-busy="true">Converting your file…</p>
            </div>
          )}

          {!loading && !result && !error && (
            <div className="empty-preview">
              <p>Your embroidery preview will appear here</p>
              <small>
                {pair
                  ? `Upload a ${pair.from.toUpperCase()} file on the left to preview it and convert it to ${pair.to.toUpperCase()}`
                  : "Upload a file on the left to preview and convert it"}
              </small>
            </div>
          )}

          {!loading && result && (
            <div className="result-preview">
              <figure className="preview-container">
                {result.files.png && (
                  <img src={mediaUrl(apiUrl, result.files.png)} alt="Embroidery preview" />
                )}
                <figcaption style={{ marginTop: "1rem", fontSize: "0.9em", color: "var(--pico-muted-color)" }}>
                  {result.width_inches.toFixed(2)}" × {result.height_inches.toFixed(2)}"
                  {" · from "}{result.source_format.toUpperCase()}
                </figcaption>
                {result.size_warning && (
                  <small style={{ display: "block", color: "var(--pico-color-amber-500)" }}>
                    ⚠️ This design was scaled down significantly and fine detail may be lost.
                  </small>
                )}
              </figure>

              {stitchReplayerEnabled && (
                <p style={{ textAlign: "center", marginTop: "0.5rem" }}>
                  {/* Prefer the asset-id route when this conversion has one:
                      it's the same design, but visiting it there is what
                      lets its own downloads offer the email/Pro prompts too,
                      since ownership (this page's token) is already settled. */}
                  <a href={`/replay/${result.asset_id || `content/${result.content_key}`}`}>Stitch Simulator</a>
                  {shareButtonsEnabled && (
                    <>
                      {"  ·  "}
                      <ShareButton path={`/replay/${result.asset_id || `content/${result.content_key}`}`} source="convert" />
                    </>
                  )}
                </p>
              )}

              <p className="studio-upsell" style={{ textAlign: "center", marginTop: "0.5rem" }}>
                <StudioLink source="convert" newTab>
                  Edit this file in Stitch Studio, free{"\u00a0"}→
                </StudioLink>
              </p>

              <DownsampleNotice
                stitchesRemoved={result.stitches_removed}
                enabled={downsample}
                disabled={loading}
                onChange={enabled => {
                  setDownsample(enabled);
                  handleSubmit(enabled);
                }}
              />
              <FormatDownloadButtons files={result.files} onDownload={handleDownload} onEmailDesign={showEmailDesignButton ? openEmailPrompt : undefined} preferred={pair?.to} />
            </div>
          )}
        </div>
      </div>

      {pair ? (
        <section className="tool-details">
          <h2>About converting {pair.from.toUpperCase()} to {pair.to.toUpperCase()}</h2>
          {pair.details.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}

          <h2>Other embroidery file conversions</h2>
          <ul className="format-list">
            {otherPairs.map((other) => (
              <li key={other.slug}>
                <a href={convertPairPath(other)}>{other.heading}</a>
              </li>
            ))}
            <li>
              <a href="/convert">All formats</a> – PES, DST, JEF, EXP, and VP3, any direction
            </li>
          </ul>
        </section>
      ) : (
        <section className="tool-details">
          <h2>Supported embroidery file formats</h2>
          <p>
            Convert between PES, DST, JEF, EXP, and VP3 embroidery files for free,
            right in your browser. Upload a design, preview it, resize it to fit
            your hoop, and download it in every format your machine reads --
            no Ink/Stitch, Hatch, or Wilcom install required.
          </p>
          <ul className="format-list">
            <li><strong>PES</strong> – Brother embroidery machines</li>
            <li><strong>DST</strong> – Tajima and commercial machines</li>
            <li><strong>JEF</strong> – Janome embroidery machines</li>
            <li><strong>EXP</strong> – Bernina embroidery machines</li>
            <li><strong>VP3</strong> – Husqvarna Viking and Pfaff machines (read only)</li>
          </ul>

          <h3>Supported conversions</h3>
          <ul className="format-list">
            <li>PES → DST, EXP, JEF</li>
            <li>DST → PES, EXP, JEF</li>
            <li>EXP → PES, DST, JEF</li>
            <li>JEF → PES, DST, EXP</li>
            <li>VP3 → PES, DST, EXP, JEF</li>
          </ul>
          <p>
            VP3 files can be uploaded and converted from, but not written to,
            since Husqvarna Viking and Pfaff use a closed format.
          </p>

          <h2>Common embroidery file conversions</h2>

          <h3><a href="/convert/pes-to-dst">PES to DST converter</a></h3>
          <p>
            PES is Brother's home-machine format; DST is the Tajima format
            most commercial and multi-needle machines read. To convert PES to
            DST online, upload the PES file above and download the DST. The
            stitches carry over as they are. DST doesn't record thread
            colors, only where each color change happens, so the machine
            will ask you to assign them.
          </p>

          <h3><a href="/convert/dst-to-pes">DST to PES</a></h3>
          <p>
            Going the other way is the usual route for a design bought or
            downloaded as DST that needs to run on a Brother machine. Upload
            the DST and download the PES. The DST had no thread colors to
            carry over, so the PES uses alternating gray placeholder
            threads, one per color block, for you to swap for real colors on
            the machine.
          </p>

          <h3><a href="/convert/jef-to-pes">JEF to PES</a></h3>
          <p>
            JEF is Janome's format. Converting JEF to PES moves a Janome
            design onto a Brother machine with the stitches and color order
            intact -- upload the JEF and download the PES.
          </p>
        </section>
      )}

      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          {faqData.map((faq, index) => (
            <div key={index} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openFAQ === index}
                type="button"
              >
                <span>{faq.question}</span>
                <span className={`faq-icon ${openFAQ === index ? 'open' : ''}`}>
                  {openFAQ === index ? '−' : '+'}
                </span>
              </button>
              <div className={`faq-answer ${openFAQ === index ? 'open' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {emailPrompt && result?.asset_id && (
        <EmailDesignModal
          apiUrl={apiUrl}
          token={token}
          assetId={result.asset_id}
          format={emailPrompt.format}
          prefilledEmail={emailPrompt.prefilledEmail}
          requested={emailPrompt.requested}
          availableFormats={Object.keys(result.files)}
          source="convert"
          onClose={closeEmailPrompt}
        />
      )}

      {/* Same sequencing as CreatePage: waits for the design-email modal to
          close (or to never have opened at all) rather than rendering
          alongside it -- see ProUpsellCard's own comment. */}
      {proUpsellVariant && !emailPrompt && emailPromptSettled && (
        <ProUpsellCard variant={proUpsellVariant} onDone={closeProUpsell} onInterested={markProUpsellInterested} />
      )}
    </>
  );
}
